"use client"

export const NOTIFICATIONS_STORAGE_KEY = "mixtape_notifications"
export const NOTIFICATION_EVENT = "mixtape:notification"
export const NOTIFICATION_READ_EVENT = "mixtape:notification-read"
const MAX_NOTIFICATIONS = 10

export type MixtapeNotification = {
  id: string
  titulo: string
  descripcion: string
  fecha: string
  imagen?: string
  color?: string
  tipo?: "grande" | "chica"
  subtipo?: string
  esRadio?: boolean
  radioId?: string
  radioNombre?: string
  inicio?: string
  fin?: string
  enlaceEvento?: string
  leida?: boolean
  creadaEn?: number
}

const createId = () =>
  `notification_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`

export function getNotifications(): MixtapeNotification[] {
  if (typeof window === "undefined") return []

  try {
    const raw = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY)
    if (!raw) return []

    const parsed = JSON.parse(raw)

    if (!Array.isArray(parsed)) return []

    return parsed
      .filter(
        (item): item is MixtapeNotification =>
          item &&
          typeof item.id === "string" &&
          typeof item.titulo === "string"
      )
      .sort((a, b) => (b.creadaEn ?? 0) - (a.creadaEn ?? 0))
  } catch {
    return []
  }
}

export const getStoredNotifications = getNotifications

function saveNotifications(notifications: MixtapeNotification[]) {
  if (typeof window === "undefined") return

  localStorage.setItem(
    NOTIFICATIONS_STORAGE_KEY,
    JSON.stringify(notifications.slice(0, MAX_NOTIFICATIONS))
  )
}

export function addNotification(
  data: Omit<MixtapeNotification, "id" | "creadaEn" | "leida">
) {
  const notification: MixtapeNotification = {
    ...data,
    id: createId(),
    creadaEn: Date.now(),
    leida: false,
  }

  const notifications = [notification, ...getNotifications()].slice(
    0,
    MAX_NOTIFICATIONS
  )

  saveNotifications(notifications)

  window.dispatchEvent(
    new CustomEvent(NOTIFICATION_EVENT, {
      detail: notification,
    })
  )

  return notification
}

export function addRadioNotification(data: {
  radioId: string
  radioNombre: string
  titulo: string
  descripcion: string
  fecha: string
  imagen?: string
  color?: string
  inicio?: string
  fin?: string
  enlaceEvento?: string
  subtipo?: string
}) {
  return addNotification({
    ...data,
    tipo: "grande",
    subtipo: data.subtipo || "radio",
    esRadio: true,
    enlaceEvento: data.enlaceEvento || "/radio",
  })
}

export function markNotificationAsRead(id: string) {
  const notifications = getNotifications().map((item) =>
    item.id === id ? { ...item, leida: true } : item
  )

  saveNotifications(notifications)

  window.dispatchEvent(
    new CustomEvent(NOTIFICATION_READ_EVENT, { detail: { id } })
  )
}

export function markAllNotificationsAsRead() {
  saveNotifications(
    getNotifications().map((item) => ({
      ...item,
      leida: true,
    }))
  )

  window.dispatchEvent(new Event(NOTIFICATION_READ_EVENT))
}

export function deleteNotification(id: string) {
  saveNotifications(
    getNotifications().filter((item) => item.id !== id)
  )

  window.dispatchEvent(
    new CustomEvent(NOTIFICATION_READ_EVENT, {
      detail: { id, deleted: true },
    })
  )
}

export const removeNotification = deleteNotification

export function clearNotifications() {
  if (typeof window === "undefined") return

  localStorage.removeItem(NOTIFICATIONS_STORAGE_KEY)
  window.dispatchEvent(new Event(NOTIFICATION_READ_EVENT))
}

export function getUnreadNotificationsCount() {
  return getNotifications().filter((item) => !item.leida).length
}

export function subscribeToNotifications(
  callback: (notification: MixtapeNotification) => void
) {
  if (typeof window === "undefined") return () => {}

  const handler = (event: Event) => {
    const notification = (event as CustomEvent<MixtapeNotification>).detail
    if (notification) callback(notification)
  }

  window.addEventListener(NOTIFICATION_EVENT, handler)

  return () => {
    window.removeEventListener(NOTIFICATION_EVENT, handler)
  }
}

export function subscribeToNotificationChanges(callback: () => void) {
  if (typeof window === "undefined") return () => {}

  const handler = () => callback()

  window.addEventListener(NOTIFICATION_EVENT, handler)
  window.addEventListener(NOTIFICATION_READ_EVENT, handler)

  return () => {
    window.removeEventListener(NOTIFICATION_EVENT, handler)
    window.removeEventListener(NOTIFICATION_READ_EVENT, handler)
  }
}

export function testNotification(radioTitle = "Mixtape Bass") {
  const now = new Date()
  const end = new Date(now.getTime() + 60 * 60 * 1000)

  return addRadioNotification({
    radioId: "test",
    radioNombre: radioTitle,
    titulo: "Nueva notificación",
    descripcion: `Este es un test de ${radioTitle}. La notificación ha llegado a Mixtape.`,
    fecha: now.toISOString(),
    inicio: now.toISOString(),
    fin: end.toISOString(),
    imagen: "/image/app/notificacion/mixtape.webp",
    color: "#007aff",
    enlaceEvento: "/radio",
  })
}

export function testRadioProgramNotification(data: {
  radioId: string
  radioNombre: string
  programTitle: string
  startTime: string
  cover?: string
  color?: string
}) {
  const now = new Date()
  const end = new Date(now.getTime() + 60 * 60 * 1000)

  return addRadioNotification({
    radioId: data.radioId,
    radioNombre: data.radioNombre,
    titulo: "Empieza pronto",
    descripcion: data.programTitle,
    fecha: now.toISOString(),
    inicio: data.startTime || now.toISOString(),
    fin: end.toISOString(),
    imagen:
      data.cover || "/image/app/notificacion/mixtape.webp",
    color: data.color || "#007aff",
    enlaceEvento: "/radio",
  })
}